import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const colorLabel = event.target.closest('label.ready-to-ship, label.made-to-order');

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.#applySizeAwareVariantId(selectedOption);

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    /** @type {{ name: string, group: string } | null} */
    const clickedSwatch = colorLabel && selectedOption instanceof HTMLInputElement
      ? { name: selectedOption.value, group: colorLabel.classList.contains('ready-to-ship') ? 'ready-to-ship' : 'made-to-order' }
      : null;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, clickedSwatch);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage && !loadsNewProduct) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    // Set the variant param to the clicked color's variant ID (from the metafield), or clear it if unknown.
    if (loadsNewProduct) {
      url.pathname = newUrl;
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    const connectedUrl = selectedOption.dataset.connectedProductUrl;
    let productUrl = connectedUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const variantId = selectedOption.dataset.variantId;

    // When navigating to a different combined listing child, send variant= with the
    // clicked swatch's variant ID. Color swatches use variant IDs as their option-value-id
    // (from color.variant_id in Liquid), and Shopify resolves variant= correctly on the
    // target product. Don't send option_values= for cross-product because these IDs
    // are variant IDs that Shopify's option_values param can't resolve.
    const currentProductUrl = this.dataset.productUrl?.split('?')[0];
    const isNavigatingCrossProduct = !!connectedUrl && connectedUrl !== currentProductUrl;

    if (variantId) {
      params.push(`variant=${variantId}`);
    }

    if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {{ name: string, group: string } | null} clickedSwatch - Info about the clicked color swatch, or null if not a color change.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false, clickedSwatch = null) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          const newProduct = this.updateMain(html, clickedSwatch);

          // Dispatch on document: after morph, 'this' is replaced and disconnected, so event would not bubble
          if (this.selectedOptionId) {
            document.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   * @property {string} [type] - Shipping type: 'ecom' (RTS) or 'mto' (Made to Order / combined-no-RTS)
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      const newProductType = newVariantPickerSource.dataset.productType;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl, type: newProductType };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
      if (newProductType) this.dataset.productType = newProductType;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   * @param {{ name: string, group: string } | null} clickedSwatch - Info about the clicked color swatch, or null if not a color change.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateMain(newHtml, clickedSwatch = null) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    // Get new product info from the variant picker in the new HTML
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    /** @type {NewProduct | undefined} */
    let newProduct;

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      const newProductType = newVariantPickerSource.dataset.productType;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl, type: newProductType };
      }
    }

    morph(main, newMain);

    // After a cross-product morph triggered by a color swatch click, the server-rendered
    // HTML will have the target product's default color selected. Re-select the color
    // the user actually clicked so the visual state matches their intent.
    if (clickedSwatch) {
      const variantPicker = document.querySelector('variant-picker');
      const groupInputs = variantPicker?.querySelectorAll(`.${clickedSwatch.group} input[type="radio"]`) ?? [];
      for (const input of groupInputs) {
        const label = input.closest('label');
        if (input instanceof HTMLInputElement && input.value === clickedSwatch.name) {
          input.checked = true;
          label?.classList.add('selected');

          // Update the heading text (e.g. "MADE TO ORDER — Emerald") to show the clicked color
          const colorSection = label?.closest('.variant-option--colors');
          const swatchValueSpan = colorSection?.querySelector('.variant-option__swatch-value');
          if (swatchValueSpan) {
            swatchValueSpan.textContent = ` ${clickedSwatch.name}`;
          }
        } else if (input instanceof HTMLInputElement) {
          input.checked = false;
          label?.classList.remove('selected');
        }
      }

      // Clear selected state and swatch-value in the OTHER color group (non-clicked group)
      const otherGroup = clickedSwatch.group === 'ready-to-ship' ? 'made-to-order' : 'ready-to-ship';
      const otherGroupInputs = variantPicker?.querySelectorAll(`.${otherGroup} input[type="radio"]`) ?? [];
      for (const input of otherGroupInputs) {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.closest('label')?.classList.remove('selected');
        }
      }
      const otherColorSection = variantPicker?.querySelector(`.${otherGroup}`)?.closest('.variant-option--colors');
      const otherSwatchValue = otherColorSection?.querySelector('.variant-option__swatch-value');
      if (otherSwatchValue) {
        otherSwatchValue.textContent = '';
      }
    }

    // Update data attributes after morph
    if (newVariantPickerSource instanceof HTMLElement) {
      this.dataset.productId = newVariantPickerSource.dataset.productId;
      this.dataset.productUrl = newVariantPickerSource.dataset.productUrl;
      if (newVariantPickerSource.dataset.productType) {
        this.dataset.productType = newVariantPickerSource.dataset.productType;
      }
    }

    // Force product-form to re-attach event listeners after morph
    document.querySelectorAll('product-form-component').forEach((form) => {
      if (form.updatedCallback) {
        form.updatedCallback();
      }
    });

    return newProduct;
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * When a color swatch with a sizes map is clicked, rewrite its data-variant-id
   * to the variant matching the currently selected size — so swapping color preserves
   * the active size instead of jumping to the color's default variant.
   * @param {Element} selectedOption
   */
  #applySizeAwareVariantId(selectedOption) {
    if (!(selectedOption instanceof HTMLInputElement)) return;
    if (selectedOption.dataset.optionName !== 'color') return;

    const sizesJson = selectedOption.dataset.sizes;
    if (!sizesJson) return;

    const sizeInput = this.querySelector('[data-option-name="size"]:checked');
    if (!(sizeInput instanceof HTMLInputElement)) return;

    const sizeValue = sizeInput.value;
    if (!sizeValue) return;

    let sizes;
    try {
      sizes = JSON.parse(sizesJson);
    } catch {
      return;
    }

    const variantId = sizes?.[sizeValue];
    if (variantId) {
      selectedOption.dataset.variantId = String(variantId);
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
